import RenderManager from "../rendering/render-manager";
import Base2DElementDynamic from "./base-2d-element-dynamic";
import Transition from "./Transition";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";

const PATH_SKETCH = "/images/sketch.svg"
let IMAGE_SKETCH = null

class Highlighter extends Base2DElementDynamic {
    get type2d() { return "Highlighter"; }
    get disableContext() {
        return true;
    }
    _createContainer() {
        const container = super._createContainer();

        container.style.position = "absolute";
        container.style.animationFillMode = "both";
        container.style.overflow = "hidden";
        container.classList.add("q3-text-editor");
        container.style.width = container.style.height = "100%";

        return container;
    }

    _initDefaultVars() {
        super._initDefaultVars();

        const { width, height } = RenderManager.getSize();
        const ar = height / width;

        this._wh = new THREE.Vector2(0.4 * ar, 0.4);
        this._highlighterType = "sketch";
        this._color = new THREE.Color(0x2262C0);
        this._opacity = 1;
    }

    _initValues(options) {
        this._highlighterType = "highlighterType" in options ? options.highlighterType : this._highlighterType;
        this._color.copy("color" in options ? new THREE.Color(options.color) : this._color);
        this._opacity = "opacity" in options ? options.opacity : this._opacity;
        this._inLetterbox = Boolean(options.inLetterbox !== false);
    }

    get opacity() { return this._opacity; }
    set opacity(v) {
        this._opacity = v;
        this.needsUpdate = true;
    }

    get color() { return this._color; }
    set color(v) {
        this._color.copy(v);
        this.needsUpdate = true;
    }

    get highlighterType() { return this._highlighterType; }
    set highlighterType(v) {
        this._highlighterType = v;
        this.needsUpdate = true;
    }

    setData(data) {
        const currentSize = this.getVWSize();
        const size = {
            x: data.x, y: data.y,
            width: data.width, height: data.height
        };

        Object.assign(this[currentSize], size);

        this.data[currentSize] = {};

        Object.assign(this.data[currentSize], size);

        this.redraw();
    }
    get clickElement() {
        return this._draggable;
    }
    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);

        this._canvas = document.createElement("canvas");
        this._ctx2d = this._canvas.getContext("2d");
        this._canvas.style.width = this._canvas.style.height = "100%";
        this.domElement.appendChild(this._canvas);

        this._draggable = document.createElement("div");
        this._draggable.style.position = "absolute";
        this._draggable.style.pointerEvents = "auto";
        // this._draggable.style.background = "rgba(255, 0, 0, 0.5)";
        this.domElement.append(this._draggable);

        if (!Q3.editMode) this._draggable.style.display = "none";

        const action = this.data.action;

        if (action && action[0] && !Q3.editMode) {
            this._draggable.classList.add("clickable");
        }

        this._draggable.addEventListener("click", e => {
            this.selectHandler(e);

            if (action && action[0] && !Q3.editMode) {
                const transition = new Transition(action[0].systemAction, action[0].value);

                transition.click();
            }
        }, false);

        this.redraw = this.redraw.bind(this);

        this.redraw();

        if (!Q3.editMode) {
            if (!Q3.isReady) {
                Events.listen("tourReady", () => {
                    this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                    this.enterExitCtrl.init(options);
                });
            } else {
                this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                this.enterExitCtrl.init(options);
            }
        } else {
            this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
            this.enterExitCtrl.init(options);
        }
    }

    toggleEvents(enabled) {
        this.disableEvents = !enabled;
        this.domElement.style.pointerEvents = enabled || Q3.editMode ? "auto" : "none";
    }

    getRenderPositionAndSize(width, height) {
        const pr = RenderManager.pixelRatio;
        const size = this.getVWSize();

        if (this[size]) { Object.assign(this, this[size]); }

        if (!width) { width = size.width; }
        if (!height) { height = size.height; }

        let w = this.width * width * pr;
        let h = this.height * height * pr;

        if (w < 40) { w = 40; }
        if (h < 40) { h = 40; }

        const [x, y] = this._calcPosition(width, height);

        return { x, y, w, h };
    }

    delete() {
        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }
    }

    redraw(width, height) {
        const pr = RenderManager.pixelRatio;

        if (!width || !height) {
            const { width: _width, height: _height } = RenderManager.getSize();

            if (!width) width = _width / pr;
            if (!height) height = _height / pr;
        }

        if (width <= 0 || height <= 0) return;

        if (!Q3.editMode && this.inLetterbox !== false) {
            width = Q3.letterbox.offsetWidth / pr;
            height = (Q3.letterbox.offsetHeight) / pr;
        }

        this._canvas.width = width * pr;
        this._canvas.height = height * pr;

        const { x, y, w, h } = this.getRenderPositionAndSize(width, height);

        Object.assign(this._draggable.style, {
            width: `${w}px`, height: `${h}px`,
            left: `${(x || 0)}px`, top: `${(y || 0)}px`
        });

        const drawObject = {
            ctx: this._ctx2d,
            lineWidth: 4,
            color: this._color, opacity: this._opacity,
            x, y, w, h,
            width: width * pr, height: height * pr
        };

        switch (this._highlighterType) {
            case "sketch":
                drawSketch(drawObject);
                break;
            case "vignette":
                drawVignette(drawObject);
                break;
            case "circle":
                drawEllpise(drawObject);
                break;
            case "glow":
                drawGlow(drawObject);
                break;
            case "rectangle":
            default:
                drawRecatngle(drawObject);
                break;
        }


        if (Q3.selected === this && this.target && Q3.editMode) {
            Events.fire("viewportchange");
        }

        if (Q3.editMode) {
            if (Q3.mobileView && this.hiddenMobile) {
                this.domElement.style.opacity = 0.4;
            } else if (!Q3.mobileView && this.hiddenDesktop) {
                this.domElement.style.opacity = 0.4;
            } else {
                this.domElement.style.opacity = 1;
            }
        }
    }
}

export default Highlighter;
export { Highlighter };

function drawSketch({ ctx, color, opacity, x, y, w, h }) {
    function draw() {
        ctx.beginPath()
        ctx.globalCompositeOperation = "destination-atop";
        ctx.fillStyle = `rgba(${color.r * 255}, ${color.g * 255}, ${color.b * 255}, 1)`;
        ctx.globalAlpha = opacity;
        ctx.fillRect(x, y, w, h);
        ctx.drawImage(IMAGE_SKETCH, x, y, w, h);
        ctx.globalCompositeOperation = "source-over";
    }

    if (!IMAGE_SKETCH) {
        IMAGE_SKETCH = new Image();
        IMAGE_SKETCH.src = PATH_SKETCH;
        IMAGE_SKETCH.onload = draw;
    }

    draw();
}

function drawEllpise({ ctx, color, opacity, x, y, w, h }) {
    w = w >> 1;
    h = h >> 1;

    ctx.beginPath();
    ctx.fillStyle = `rgba(${color.r * 255}, ${color.g * 255}, ${color.b * 255}, ${opacity})`;
    ctx.ellipse(x + w, y + h, w, h, 0, 0, Math.PI * 2);
    ctx.fill();
}

function drawRecatngle({ ctx, color, opacity, x, y, w, h }) {
    ctx.beginPath();
    ctx.fillStyle = `rgba(${color.r * 255}, ${color.g * 255}, ${color.b * 255}, ${opacity})`;
    ctx.fillRect(x, y, w, h);
}

function drawGlow({ ctx, color, opacity, x, y, w, h }) {
    w = w >> 1;
    h = h >> 1;

    const r = Math.max(w, h);
    const gradient = ctx.createRadialGradient(x + w, y + h, 1, x + w, y + h, r);

    const rgb = color.toArray().map(x => x * 255).join(", ");

    gradient.addColorStop(0, `rgba(${rgb}, ${opacity})`);
    gradient.addColorStop(1, `rgba(${rgb}, 0)`);

    ctx.fillStyle = gradient;

    ctx.beginPath();
    ctx.ellipse(x + w, y + h, w, h, 0, 0, Math.PI * 2);
    ctx.fill();
}

function drawVignette({ ctx, color, opacity, x, y, w, h, width, height }) {
    w = w >> 1;
    h = h >> 1;

    ctx.beginPath();
    ctx.fillStyle = `rgba(${color.r * 255}, ${color.g * 255}, ${color.b * 255}, ${opacity})`;
    ctx.fillRect(0, 0, width, height);

    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.fillStyle = "white";
    ctx.ellipse(x + w, y + h, w, h, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    const r = Math.max(w, h);
    const gradient = ctx.createRadialGradient(x + w, y + h, r >> 1, x + w, y + h, r + 10);

    const rgb = color.toArray().map(x => x * 255).join(", ");

    gradient.addColorStop(0, `rgba(${rgb}, 0)`);
    gradient.addColorStop(1, `rgba(${rgb}, ${opacity})`);

    ctx.fillStyle = gradient;
    ctx.fill();
}